import { useEffect, useState } from 'react';
import { useInView as useInViewObserver } from 'react-intersection-observer';

type Props = {
  /** Number between `0` and `1` indicating the percentage that should be visible before triggering. Can also be an `array` of numbers, to create multiple trigger points. */
  threshold?: number;
  /** Number in ms to recheck after the delay if the component is still inView, before returning the `inView` status */
  delay?: number;
};

/**
 * React Hooks make it easy to monitor the `inView` state of your components.
 * Call the `useInView` hook with the (optional) options:
 * threshold: to control how much of the component have to be in view
 * delay: to recheck after the delay if the component is still inView,
 * before returninf the `inView` status
 *
 * It will return an array containing a `ref` and the `inView` status.
 * Assign the `ref` to the DOM element you want to monitor, and the hook will report the status.
 */
export function useInView({ delay = 0, threshold = 0 }: Props): {
  ref: (node?: Element | null) => void;
  inView: boolean;
} {
  const { ref, inView } = useInViewObserver({ threshold });
  const [inViewDelayFinished, setInViewDelayFinished] = useState(false);

  useEffect(() => {
    let inViewTimeout;
    if (inView) {
      clearTimeout(inViewTimeout);
      inViewTimeout = setTimeout(() => {
        setInViewDelayFinished(true);
      }, delay);
    }
    return () => {
      clearTimeout(inViewTimeout);
    };
  }, [inView, delay]);

  return { ref, inView: inView && inViewDelayFinished };
}
